import type {
  AIActionKey,
  AIActionDefinition,
  FeatureKey,
  ModelPolicy,
  PaidPlanTier,
  PlanSet,
  PlanTier,
  SubscriptionCatalog,
} from '../../../types/src/subscription';

export const PLAN_TIER_ORDER: readonly PlanTier[] = [
  'free',
  'plus',
  'pro',
] as const;
export const PLAN_SETS: readonly PlanSet[] = [
  'individual',
  'employer',
] as const;
export const PAID_PLAN_TIERS: readonly PaidPlanTier[] = [
  'plus',
  'pro',
] as const;

export function normalizeLegacyPlanTier(plan?: string | null): PlanTier {
  const normalized = plan?.trim().toLowerCase() || 'free';

  // Legacy Premium Profile was the predecessor to the individual paid plan.
  // Existing paid customers must retain at least Plus access during migration.
  if (normalized === 'premium_profile' || normalized === 'premium-profile') {
    return 'plus';
  }

  if (normalized === 'business') {
    return 'pro';
  }

  if (PLAN_TIER_ORDER.includes(normalized as PlanTier)) {
    return normalized as PlanTier;
  }

  return 'free';
}

export function comparePlanTiers(left: PlanTier, right: PlanTier) {
  return PLAN_TIER_ORDER.indexOf(left) - PLAN_TIER_ORDER.indexOf(right);
}

export function getHighestPlanTier(
  tiers: Array<PlanTier | string | null | undefined>,
): PlanTier {
  return tiers.reduce<PlanTier>((highest, tier) => {
    const nextTier = normalizeLegacyPlanTier(tier);
    return comparePlanTiers(nextTier, highest) > 0 ? nextTier : highest;
  }, 'free');
}

export function getModelPolicyForTier(tier: PlanTier): ModelPolicy {
  return tier === 'free' ? 'sonnet_only' : 'sonnet_plus_opus_auto';
}

function usd(
  amount: number,
  suffix: string,
  billed?: { amount: number; display: string },
) {
  return {
    amount,
    currency: 'USD' as const,
    display: `$${amount.toFixed(2)}${suffix}`,
    ...(billed
      ? { billedAmount: billed.amount, billedDisplay: billed.display }
      : {}),
  };
}

function aed(amount: number, suffix: string) {
  return {
    amount,
    currency: 'AED' as const,
    display: `AED ${amount.toFixed(0)}${suffix}`,
  };
}

function feature(
  key: FeatureKey,
  planSet: PlanSet,
  label: string,
  description: string,
  minimumTier: PlanTier,
  options?: { usesWorkspaceTier?: boolean; requiresPaidSeat?: boolean },
) {
  return {
    key,
    planSet,
    label,
    description,
    minimumTier,
    ...options,
  };
}

function aiAction(
  key: AIActionKey,
  label: string,
  description: string,
  options: Omit<AIActionDefinition, 'label' | 'description'>,
) {
  return {
    key,
    label,
    description,
    planSet: options.planSet,
    featureKey: options.featureKey,
    countsTowardDaily: options.countsTowardDaily,
    estimatedCredits: options.estimatedCredits,
    allowPartialCredits: options.allowPartialCredits,
    meterSubject: options.meterSubject,
    availableToEmployerFreeWorkspace: options.availableToEmployerFreeWorkspace,
    requiresPaidSeat: options.requiresPaidSeat,
    premiumOnComplexOnly: options.premiumOnComplexOnly,
    customerAllowances: options.customerAllowances,
    dailyQuota: options.dailyQuota,
    includedDaily: options.includedDaily,
    upgradePromptKey: options.upgradePromptKey,
  };
}

export const subscriptionCatalog: SubscriptionCatalog = {
  version: 2,
  legacyPlanNormalization: {
    free: 'free',
    plus: 'plus',
    pro: 'pro',
    business: 'pro',
    premium_profile: 'plus',
    'premium-profile': 'plus',
  },
  planSets: {
    individual: {
      id: 'individual',
      label: 'Individual',
      description: 'AI-powered career tools for jobseekers and freelancers.',
      defaultTier: 'free',
      seatBased: false,
      tiers: {
        free: {
          tier: 'free',
          label: 'Free',
          shortLabel: 'Free',
          description:
            'Build your career profile and experience real AI value for free.',
          billing: {
            monthly: { prices: [aed(0, '/mo')] },
          },
          features: [
            'individual.public_profile',
            'individual.standard_branding',
            'individual.basic_cv_tips',
            'individual.career_agent_chat',
            'individual.skill_run',
            'individual.skill_run_premium',
            'individual.skills_create',
            'individual.skills_publish',
            'individual.image_generation',
            'individual.uae_pass',
          ],
          limits: {
            // Credits are the single customer-facing usage currency for
            // EVERYTHING (chat, previews, automation drafts). Free is a daily
            // drip only (no monthly pool, no rollover). Fair use (2026-09-25):
            // 70/day guarantees at least 7 agent replies even at the 10-credit
            // per-reply cap (typically 12-20 at measured usage), and the agent
            // capabilities come as a daily sample that costs no credits
            // (`includedDaily`: 1 image, 3 files, 5 web searches, 5 edits), so
            // trying them never eats the messages.
            aiActionsPerDay: 70,
            aiCreditsPerCycle: null,
            aiCreditsPerDay: 70,
            // Connectors (M3): Free gets "just a taste": 2 Tools + 1 AI.
            connectedToolsMax: 2,
            connectedAiMax: 1,
            // Nova drafts/previews are available on Free; only active schedules
            // consume this Pro-gated automation capacity.
            activeAutomationsMax: 0,
            // Memory Graph (M5): base window/cap; extended via individual.extended_memory (Pro).
            memoryGraphNodeCap: 1_000,
            memoryGraphWindowDays: 30,
            // Published sites (agent artifacts Phase 2, plan 5.6 and Q1, Q2):
            // one live site with the "Made with Tabbio" badge.
            sites: {
              liveSites: 1,
              deploysPerDay: 10,
              deployBytes: 10 * 1024 * 1024,
              hideBadge: false,
            },
          },
          modelPolicy: 'sonnet_only',
          limitBehavior: 'hard_stop',
          marketing: {
            headline: 'Start free',
            subheadline:
              'Build your digital CV, explore every tool, and try real AI assistance.',
            cta: 'Continue free',
          },
        },
        plus: {
          tier: 'plus',
          label: 'Plus',
          shortLabel: 'Plus',
          description:
            'More included AI usage plus better branding and analytics.',
          badge: '✦',
          billing: {
            monthly: { prices: [usd(14.99, '/mo')] },
            yearly: {
              prices: [
                usd(9.99, '/mo', {
                  amount: 119.99,
                  display: '$119.99/year',
                }),
              ],
            },
          },
          features: [
            'individual.public_profile',
            'individual.standard_branding',
            'individual.custom_branding',
            'individual.basic_cv_tips',
            'individual.cv_fit_report',
            'individual.application_autofill',
            'individual.cv_writer',
            'individual.cover_letter',
            'individual.application_packet',
            'individual.bio_generator',
            'individual.career_agent_chat',
            'individual.download_any_format',
            'individual.basic_analytics',
            'individual.sources_analytics',
            'individual.higher_visibility',
            'individual.skill_run',
            'individual.skill_run_premium',
            'individual.skills_create',
            'individual.skills_publish',
            'individual.image_generation',
            'individual.uae_pass',
          ],
          limits: {
            aiActionsPerDay: 300,
            aiCreditsPerCycle: 10_000,
            aiCreditsPerDay: 300,
            // Connectors (M3): Plus unlocks unlimited Tools; AI rail stays 1.
            connectedToolsMax: null,
            connectedAiMax: 1,
            // Plus can explore and keep Nova drafts; active schedules are Pro.
            activeAutomationsMax: 0,
            // Memory Graph (M5): Plus keeps the base 1K/30d window (Pro-only extension).
            memoryGraphNodeCap: 1_000,
            memoryGraphWindowDays: 30,
            // Published sites (plan 5.6): Plus may hide the badge.
            sites: {
              liveSites: 10,
              deploysPerDay: 50,
              deployBytes: 25 * 1024 * 1024,
              hideBadge: true,
            },
          },
          modelPolicy: 'sonnet_plus_opus_auto',
          limitBehavior: 'hard_stop',
          marketing: {
            eyebrow: 'Tabbio Plus',
            headline: 'Do more with smarter AI',
            subheadline:
              'More included AI usage, stronger branding, and richer visibility.',
            cta: 'Upgrade to Plus',
            highlight: '10,000 credits a month for an active job search',
          },
        },
        pro: {
          tier: 'pro',
          label: 'Pro',
          shortLabel: 'Pro',
          description:
            'Autopilot career workflows, deeper insights, and premium routing.',
          badge: '◆',
          billing: {
            monthly: { prices: [usd(29.99, '/mo')] },
            yearly: {
              prices: [
                usd(24.99, '/mo', {
                  amount: 299.99,
                  display: '$299.99/year',
                }),
              ],
            },
          },
          features: [
            'individual.public_profile',
            'individual.standard_branding',
            'individual.custom_branding',
            'individual.custom_domain',
            'individual.basic_cv_tips',
            'individual.cv_fit_report',
            'individual.application_autofill',
            'individual.cv_writer',
            'individual.cover_letter',
            'individual.application_packet',
            'individual.bio_generator',
            'individual.career_agent_chat',
            'individual.download_any_format',
            'individual.basic_analytics',
            'individual.sources_analytics',
            'individual.higher_visibility',
            'individual.priority_visibility',
            'individual.autopilot_career_agent',
            'individual.interview_prep',
            'individual.salary_insights',
            'individual.recruiter_preview',
            'individual.extended_memory',
            'individual.recruiter_activity',
            'individual.skill_run',
            'individual.skill_run_premium',
            'individual.skills_create',
            'individual.skills_publish',
            'individual.automations',
            'individual.image_generation',
            'individual.uae_pass',
            'individual.uae_pass_priority',
          ],
          limits: {
            aiActionsPerDay: 3000,
            aiCreditsPerCycle: 100_000,
            aiCreditsPerDay: 3000,
            // Connectors (M3): Pro unlocks unlimited Tools + AI.
            connectedToolsMax: null,
            connectedAiMax: null,
            // Automation/Nova (M4): Pro unlocks automations. Abuse cap on the
            // number of ACTIVE automations. TUNABLE: override at the enforcement
            // site (planner/activate) via env (e.g. NOVA_ACTIVE_AUTOMATIONS_MAX).
            activeAutomationsMax: 10,
            // Memory Graph (M5): Pro unlocks the extended window/cap (gated by the
            // individual.extended_memory feature key, which Pro carries).
            memoryGraphNodeCap: 100_000,
            memoryGraphWindowDays: 365,
            // Published sites (plan 5.6).
            sites: {
              liveSites: 50,
              deploysPerDay: 200,
              deployBytes: 50 * 1024 * 1024,
              hideBadge: true,
            },
          },
          modelPolicy: 'sonnet_plus_opus_auto',
          limitBehavior: 'hard_stop',
          marketing: {
            eyebrow: 'Tabbio Pro',
            headline: 'Let an agent take it from here',
            subheadline:
              'Autopilot workflows, deeper memory, and the highest included AI usage.',
            cta: 'Upgrade to Pro',
            highlight: '100,000 credits a month plus Career agent automation',
          },
        },
      },
    },
    employer: {
      id: 'employer',
      label: 'Employer',
      description:
        'Workspace subscriptions with paid AI seats and shared non-AI access.',
      defaultTier: 'free',
      seatBased: true,
      workspaceFreePool: {
        aiActionsPerDay: 3,
        eligibleActions: ['employer.workspace_basic_ai'],
      },
      tiers: {
        free: {
          tier: 'free',
          label: 'Free',
          shortLabel: 'Free',
          description:
            'Unlimited free members, one hiring page, one job post, shared basic AI pool.',
          billing: {
            monthly: { prices: [aed(0, '/mo')] },
          },
          features: [
            'employer.hiring_pages',
            'employer.job_posts',
            'employer.community_support',
            'employer.career_fair_qr',
          ],
          limits: {
            aiActionsPerDay: 3,
            activeHiringPages: 1,
            activeJobPosts: 1,
          },
          modelPolicy: 'sonnet_only',
          limitBehavior: 'hard_stop',
          marketing: {
            headline: 'Start hiring for free',
            subheadline:
              'Unlimited members, basic hiring pages, and a small shared AI pool.',
            cta: 'Continue free',
          },
        },
        plus: {
          tier: 'plus',
          label: 'Plus',
          shortLabel: 'Plus',
          description:
            'Per-seat AI for recruiters with team analytics and better hiring capacity.',
          badge: '✦',
          billing: {
            monthly: { prices: [usd(49.99, '/seat/mo')] },
            yearly: {
              prices: [
                usd(39.99, '/seat/mo', {
                  amount: 479.99,
                  display: '$479.99/seat/year',
                }),
              ],
            },
          },
          features: [
            'employer.hiring_pages',
            'employer.job_posts',
            'employer.email_support',
            'employer.career_fair_qr',
            'employer.team_analytics',
            'employer.private_contact_visibility',
            'employer.outreach',
            'employer.candidate_ranking',
            'employer.jd_generator',
          ],
          limits: {
            aiActionsPerDay: 50,
            activeHiringPages: 5,
            activeJobPosts: 10,
          },
          modelPolicy: 'sonnet_plus_opus_auto',
          limitBehavior: 'soft_throttle',
          marketing: {
            eyebrow: 'Employer Plus',
            headline: 'Give recruiters AI seats',
            subheadline:
              'Per-seat AI for candidate ranking, JD generation, and team analytics.',
            cta: 'Add Plus seats',
            highlight: '50 AI actions/day per seat',
          },
        },
        pro: {
          tier: 'pro',
          label: 'Pro',
          shortLabel: 'Pro',
          description:
            'Autopilot hiring, bulk workflows, and premium support for serious teams.',
          badge: '◆',
          billing: {
            monthly: { prices: [usd(99.99, '/seat/mo')] },
            yearly: {
              prices: [
                usd(79.99, '/seat/mo', {
                  amount: 959.99,
                  display: '$959.99/seat/year',
                }),
              ],
            },
          },
          features: [
            'employer.hiring_pages',
            'employer.job_posts',
            'employer.priority_support',
            'employer.career_fair_qr',
            'employer.career_fair_qr_analytics',
            'employer.team_analytics',
            'employer.job_visibility_boost',
            'employer.private_contact_visibility',
            'employer.outreach',
            'employer.candidate_ranking',
            'employer.jd_generator',
            'employer.autopilot_hiring',
            'employer.bulk_screening',
            'employer.talent_insights',
            'employer.extended_memory',
          ],
          limits: {
            aiActionsPerDay: 200,
            activeHiringPages: null,
            activeJobPosts: null,
          },
          modelPolicy: 'sonnet_plus_opus_auto',
          limitBehavior: 'slowdown',
          marketing: {
            eyebrow: 'Employer Pro',
            headline: 'Put hiring on autopilot',
            subheadline:
              'Bulk screening, talent insights, and premium support for the whole workspace.',
            cta: 'Add Pro seats',
            highlight: '200 AI actions/day per seat',
          },
        },
      },
    },
  },
  features: {
    'individual.public_profile': feature(
      'individual.public_profile',
      'individual',
      'Public profile',
      'Your full public CV at username.tabbio.com.',
      'free',
    ),
    'individual.standard_branding': feature(
      'individual.standard_branding',
      'individual',
      'Standard branding',
      'Default Tabbio branding on public profile surfaces.',
      'free',
    ),
    'individual.custom_branding': feature(
      'individual.custom_branding',
      'individual',
      'Custom branding',
      'Personalized profile branding and styling.',
      'plus',
    ),
    'individual.custom_domain': feature(
      'individual.custom_domain',
      'individual',
      'Custom domain',
      'Connect a custom domain to a public profile.',
      'pro',
    ),
    'individual.basic_cv_tips': feature(
      'individual.basic_cv_tips',
      'individual',
      'Basic CV tips',
      'Lightweight AI guidance for CV improvements.',
      'free',
    ),
    'individual.cv_fit_report': feature(
      'individual.cv_fit_report',
      'individual',
      'CV fit report',
      'Generate paid GCC fit, ATS, and recruiter-readiness reports.',
      'plus',
    ),
    'individual.application_autofill': feature(
      'individual.application_autofill',
      'individual',
      'Unlimited SmartApply autofill',
      'Fill employer application forms and attach a selected CV with SmartApply.',
      'plus',
    ),
    'individual.cv_writer': feature(
      'individual.cv_writer',
      'individual',
      'CV writer',
      'Generate and rewrite CV sections with AI.',
      'plus',
    ),
    // ── Skills Hub (M2) ──────────────────────────────────────────────
    // Skills are free at every tier: installing, running, creating, and
    // publishing never require a paid plan. Usage is governed by credits
    // alone (the skill_run AI actions below), so the only ceiling a Free
    // user hits is the daily credit drip, never a subscription paywall.
    'individual.skill_run': feature(
      'individual.skill_run',
      'individual',
      'Install and run skills',
      'Install Agent Skills (SKILL.md) and run them on your CV.',
      'free',
    ),
    'individual.skill_run_premium': feature(
      'individual.skill_run_premium',
      'individual',
      'Run premium skills',
      'Run premium-tier Agent Skills with premium model routing.',
      'free',
    ),
    'individual.skills_create': feature(
      'individual.skills_create',
      'individual',
      'Create skills',
      'Author your own private Agent Skills.',
      'free',
    ),
    'individual.skills_publish': feature(
      'individual.skills_publish',
      'individual',
      'Publish skills',
      'Publish authored skills to the community shelf (Phase 2).',
      'free',
    ),
    // ── Connectors (M3) ──────────────────────────────────────────────
    // Available to everyone; the per-rail caps (connectedToolsMax /
    // connectedAiMax) are what gate Free at 2 Tools + 1 AI and unlock at Plus.
    'individual.connectors': feature(
      'individual.connectors',
      'individual',
      'Connectors',
      'Connect tools (Gmail, Slack, Zapier, Make) and AI clients (ChatGPT, Claude) so Tabbio can access and act on your data.',
      'free',
    ),
    // ── Automation / Nova (M4) ───────────────────────────────────────────────
    // Pro-only: plain-English → planner → versioned automation DSL → draft →
    // approve → scheduled run. Free/Plus hit the Nova upgrade wall.
    'individual.automations': feature(
      'individual.automations',
      'individual',
      'Automation',
      'Describe a workflow in plain English and build a scheduled automation you approve, with drafts you always review before anything is sent.',
      'pro',
    ),
    // ── Agent capabilities (Phase 1) ─────────────────────────────────────────
    // Research and document rendering run on plain credits at every tier.
    // Image generation is a paid provider call: Free gets a daily sample of
    // one image (fair use, 2026-09-25; `image_generate.dailyQuota`), Plus and
    // Pro the full quota.
    'individual.image_generation': feature(
      'individual.image_generation',
      'individual',
      'Image generation',
      'Create images with the career agent, in chat and in Tasks.',
      'free',
    ),
    'individual.cover_letter': feature(
      'individual.cover_letter',
      'individual',
      'Cover letter generator',
      'Generate tailored cover letters.',
      'plus',
    ),
    'individual.application_packet': feature(
      'individual.application_packet',
      'individual',
      'Application packet',
      'Generate saved job-specific application assets.',
      'plus',
    ),
    'individual.bio_generator': feature(
      'individual.bio_generator',
      'individual',
      'Bio generator',
      'Create professional bios from profile data.',
      'plus',
    ),
    'individual.career_agent_chat': feature(
      'individual.career_agent_chat',
      'individual',
      'Career agent chat',
      'Conversational career agent guidance.',
      'free',
    ),
    'individual.download_any_format': feature(
      'individual.download_any_format',
      'individual',
      'Download any format',
      'Export content in multiple downloadable formats.',
      'plus',
    ),
    'individual.basic_analytics': feature(
      'individual.basic_analytics',
      'individual',
      'Detailed analytics',
      'See visitor details and richer profile and public page insights.',
      'plus',
    ),
    'individual.sources_analytics': feature(
      'individual.sources_analytics',
      'individual',
      'Views and sources analytics',
      'Understand where profile traffic comes from.',
      'plus',
    ),
    'individual.higher_visibility': feature(
      'individual.higher_visibility',
      'individual',
      'Higher visibility',
      'Increase discoverability to employers.',
      'plus',
    ),
    'individual.priority_visibility': feature(
      'individual.priority_visibility',
      'individual',
      'Priority visibility',
      'Gain stronger ranking and visibility boosts.',
      'pro',
    ),
    'individual.autopilot_career_agent': feature(
      'individual.autopilot_career_agent',
      'individual',
      'Autopilot career agent',
      'Access autonomous career support workflows.',
      'pro',
    ),
    'individual.interview_prep': feature(
      'individual.interview_prep',
      'individual',
      'Interview prep',
      'Use AI to prepare for interviews.',
      'pro',
    ),
    'individual.salary_insights': feature(
      'individual.salary_insights',
      'individual',
      'Salary insights',
      'Compare salary expectations with AI assistance.',
      'pro',
    ),
    'individual.recruiter_preview': feature(
      'individual.recruiter_preview',
      'individual',
      'Recruiter preview',
      'Preview how employers may evaluate a profile and application.',
      'pro',
    ),
    'individual.extended_memory': feature(
      'individual.extended_memory',
      'individual',
      'Extended agent memory',
      'Retain more career context across AI sessions.',
      'pro',
    ),
    'individual.recruiter_activity': feature(
      'individual.recruiter_activity',
      'individual',
      'Recruiter activity',
      'See more advanced recruiter activity analytics.',
      'pro',
    ),
    'individual.uae_pass': feature(
      'individual.uae_pass',
      'individual',
      'UAE Pass',
      'Use UAE Pass in the product.',
      'free',
    ),
    'individual.uae_pass_priority': feature(
      'individual.uae_pass_priority',
      'individual',
      'UAE Pass priority',
      'Get faster UAE Pass-related handling.',
      'pro',
    ),
    'employer.hiring_pages': feature(
      'employer.hiring_pages',
      'employer',
      'Hiring pages',
      'Active hiring pages for a company workspace.',
      'free',
      { usesWorkspaceTier: true },
    ),
    'employer.job_posts': feature(
      'employer.job_posts',
      'employer',
      'Job posts',
      'Active job post capacity for a company workspace.',
      'free',
      { usesWorkspaceTier: true },
    ),
    'employer.community_support': feature(
      'employer.community_support',
      'employer',
      'Community support',
      'Community support for free employer workspaces.',
      'free',
      { usesWorkspaceTier: true },
    ),
    'employer.email_support': feature(
      'employer.email_support',
      'employer',
      'Email support',
      'Direct support through email.',
      'plus',
      { usesWorkspaceTier: true },
    ),
    'employer.priority_support': feature(
      'employer.priority_support',
      'employer',
      'Priority support',
      'Priority support and customer success coverage.',
      'pro',
      { usesWorkspaceTier: true },
    ),
    'employer.career_fair_qr': feature(
      'employer.career_fair_qr',
      'employer',
      'Career fair QR',
      'Generate recruiting QR surfaces for events.',
      'free',
      { usesWorkspaceTier: true },
    ),
    'employer.career_fair_qr_analytics': feature(
      'employer.career_fair_qr_analytics',
      'employer',
      'Career fair QR analytics',
      'Track scans and engagement on recruiting QR assets.',
      'pro',
      { usesWorkspaceTier: true },
    ),
    'employer.team_analytics': feature(
      'employer.team_analytics',
      'employer',
      'Team analytics',
      'Aggregate hiring team analytics.',
      'plus',
      { usesWorkspaceTier: true },
    ),
    'employer.job_visibility_boost': feature(
      'employer.job_visibility_boost',
      'employer',
      '2× job visibility',
      'Boost job post visibility in search and discovery.',
      'pro',
      { usesWorkspaceTier: true },
    ),
    'employer.private_contact_visibility': feature(
      'employer.private_contact_visibility',
      'employer',
      'Private contact visibility',
      'Reveal private candidate contact methods in the employer directory.',
      'plus',
      { usesWorkspaceTier: true },
    ),
    'employer.outreach': feature(
      'employer.outreach',
      'employer',
      'Recruiter outreach',
      'Send outreach messages to candidates from the employer workspace.',
      'plus',
      { usesWorkspaceTier: true },
    ),
    'employer.candidate_ranking': feature(
      'employer.candidate_ranking',
      'employer',
      'Candidate ranking',
      'Use AI to rank candidates for a role.',
      'plus',
      { requiresPaidSeat: true },
    ),
    'employer.jd_generator': feature(
      'employer.jd_generator',
      'employer',
      'JD generator',
      'Generate job descriptions with AI.',
      'plus',
      { requiresPaidSeat: true },
    ),
    'employer.autopilot_hiring': feature(
      'employer.autopilot_hiring',
      'employer',
      'Autopilot hiring',
      'Run autonomous hiring workflows.',
      'pro',
      { requiresPaidSeat: true },
    ),
    'employer.bulk_screening': feature(
      'employer.bulk_screening',
      'employer',
      'Bulk screening',
      'Screen candidates in bulk with AI.',
      'pro',
      { requiresPaidSeat: true },
    ),
    'employer.talent_insights': feature(
      'employer.talent_insights',
      'employer',
      'Talent insights',
      'Get deeper AI insights on a talent pipeline.',
      'pro',
      { requiresPaidSeat: true },
    ),
    'employer.extended_memory': feature(
      'employer.extended_memory',
      'employer',
      'Extended agent memory',
      'Retain more hiring context in AI workflows.',
      'pro',
      { requiresPaidSeat: true },
    ),
  },
  aiActions: {
    'individual.cv_section_preview': aiAction(
      'individual.cv_section_preview',
      'CV section preview',
      'Generate a user-visible CV section preview.',
      {
        key: 'individual.cv_section_preview',
        planSet: 'individual',
        countsTowardDaily: true,
        estimatedCredits: 12,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    // Browser extension actions. Discovery and tailoring stay credit-metered;
    // SmartApply autofill has its own recognizable monthly Free sample below.
    'individual.jobs_live_refresh': aiAction(
      'individual.jobs_live_refresh',
      'Fetch fresh jobs',
      'Search job boards live for roles matching your profile.',
      {
        key: 'individual.jobs_live_refresh',
        planSet: 'individual',
        countsTowardDaily: true,
        // Unlike the other actions this one buys live third-party search
        // requests rather than model tokens, so the cost is real money per tap
        // regardless of how much text comes back. Priced above a fit check to
        // keep a frustrated user from spamming it at the provider's expense.
        estimatedCredits: 25,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.extension_fit_check': aiAction(
      'individual.extension_fit_check',
      'Job fit check',
      'Score the signed-in CV against a job posting open in the browser.',
      {
        key: 'individual.extension_fit_check',
        planSet: 'individual',
        countsTowardDaily: true,
        // Cheapest of the three: one small completion, and the hook that leads
        // into tailoring.
        estimatedCredits: 15,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.extension_tailor': aiAction(
      'individual.extension_tailor',
      'Tailor CV to a job page',
      'Rewrite the CV around a job posting captured by the browser extension.',
      {
        key: 'individual.extension_tailor',
        planSet: 'individual',
        countsTowardDaily: true,
        estimatedCredits: 30,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.extension_autofill_assist': aiAction(
      'individual.extension_autofill_assist',
      'Application autofill assist',
      'Answer screening questions and resolve ambiguous application fields.',
      {
        key: 'individual.extension_autofill_assist',
        planSet: 'individual',
        featureKey: 'individual.application_autofill',
        countsTowardDaily: true,
        // Base cost. The service commits a larger figure that scales with how
        // many questions the form actually asked.
        estimatedCredits: 20,
        allowPartialCredits: true,
        meterSubject: 'individual_user',
        customerAllowances: {
          free: {
            limit: 10,
            cadence: 'month',
            unit: 'autofill',
            label: 'SmartApply autofills',
          },
        },
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    cv_fit_report_generate: aiAction(
      'cv_fit_report_generate',
      'CV fit report',
      'Generate a paid GCC fit and recruiter-readiness report.',
      {
        key: 'cv_fit_report_generate',
        planSet: 'individual',
        featureKey: 'individual.cv_fit_report',
        countsTowardDaily: true,
        estimatedCredits: 80,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.cv_writer': aiAction(
      'individual.cv_writer',
      'CV writer',
      'Generate or rewrite CV content.',
      {
        key: 'individual.cv_writer',
        planSet: 'individual',
        featureKey: 'individual.cv_writer',
        countsTowardDaily: true,
        estimatedCredits: 60,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.cover_letter': aiAction(
      'individual.cover_letter',
      'Cover letter generation',
      'Generate a cover letter.',
      {
        key: 'individual.cover_letter',
        planSet: 'individual',
        featureKey: 'individual.cover_letter',
        countsTowardDaily: true,
        estimatedCredits: 45,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    // ── Skills Hub (M2) ──────────────────────────────────────────────
    // Credit estimates are TUNABLE. A standard skill run is a multi-tool CV
    // operation roughly on par with the CV writer (60); the premium variant
    // adds Gemini Pro routing on top, so it costs more. Skills carry no
    // subscription gate: any tier can install and run them, and credits are
    // the only limiter (Free users spend their daily drip).
    'individual.skill_run': aiAction(
      'individual.skill_run',
      'Skill run',
      'Run an Agent Skill on the user’s CV.',
      {
        key: 'individual.skill_run',
        planSet: 'individual',
        featureKey: 'individual.skill_run',
        countsTowardDaily: true,
        estimatedCredits: 40,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.skill_run',
      },
    ),
    'individual.skill_run_premium': aiAction(
      'individual.skill_run_premium',
      'Premium skill run',
      'Run a premium (Plus/Pro) Agent Skill with premium model routing.',
      {
        key: 'individual.skill_run_premium',
        planSet: 'individual',
        featureKey: 'individual.skill_run_premium',
        countsTowardDaily: true,
        estimatedCredits: 90,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.skill_run',
      },
    ),
    // ── Automation / Nova (M4) ───────────────────────────────────────────────
    // Credit estimates are TUNABLE. nova_compile is one structured-planner LLM
    // call (~CV-section size). nova_run is a per-run executor budget (planner +
    // executor LLM steps); the real per-run cost is metered by the executor
    // (M4.2): this catalog value is the reservation/abuse ceiling.
    // Compiling drafts carries no feature gate: anyone can build an automation
    // on plain credits, and activation stays Pro (individual.automations).
    'individual.nova_compile': aiAction(
      'individual.nova_compile',
      'Automation compile',
      'Compile a plain-English sentence into a Career agent automation plan.',
      {
        key: 'individual.nova_compile',
        planSet: 'individual',
        countsTowardDaily: true,
        estimatedCredits: 12,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.nova',
      },
    ),
    'individual.nova_run': aiAction(
      'individual.nova_run',
      'Automation run',
      'Run a scheduled Career agent automation (planner + executor LLM steps).',
      {
        key: 'individual.nova_run',
        planSet: 'individual',
        featureKey: 'individual.automations',
        countsTowardDaily: true,
        estimatedCredits: 40,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.nova',
      },
    ),
    // ── Agent capabilities (Phase 1) ─────────────────────────────────────────
    // Credit estimates are TUNABLE. web_research is one grounded provider call
    // (search, URL, PDF or repository read, capped at a few billed searches);
    // image_generate is one image model call; document_render is the PDF/PPTX
    // render job for one document. `dailyQuota` caps completed calls per day
    // per tier on top of credits (CapabilityMeteringService enforces it).
    'individual.web_research': aiAction(
      'individual.web_research',
      'Web research',
      'Search the web or read a page, PDF, or repository with citations.',
      {
        key: 'individual.web_research',
        planSet: 'individual',
        countsTowardDaily: true,
        estimatedCredits: 8,
        meterSubject: 'individual_user',
        dailyQuota: { free: 5, plus: 40, pro: 150 },
        includedDaily: { free: 5 },
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.image_generate': aiAction(
      'individual.image_generate',
      'Image generation',
      'Generate one image with the career agent.',
      {
        key: 'individual.image_generate',
        planSet: 'individual',
        featureKey: 'individual.image_generation',
        countsTowardDaily: true,
        estimatedCredits: 25,
        meterSubject: 'individual_user',
        dailyQuota: { free: 1, plus: 10, pro: 40 },
        includedDaily: { free: 1 },
        upgradePromptKey: 'individual.plus',
      },
    ),
    'individual.document_render': aiAction(
      'individual.document_render',
      'Document render',
      'Render an agent document as HTML, PDF, or slides.',
      {
        key: 'individual.document_render',
        planSet: 'individual',
        countsTowardDaily: true,
        estimatedCredits: 6,
        meterSubject: 'individual_user',
        dailyQuota: { free: 3, plus: 30, pro: 100 },
        includedDaily: { free: 3 },
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    // Agent artifacts Phase 2: one agent edit of a page, document or deck
    // (updatePage, updateDocument, apply or suggest), a writer model call
    // plus the HTML render. Image edits are metered as image_generate.
    'individual.artifact_edit': aiAction(
      'individual.artifact_edit',
      'Agent file edit',
      'Change or review a page, document, or deck the career agent made.',
      {
        key: 'individual.artifact_edit',
        planSet: 'individual',
        countsTowardDaily: true,
        estimatedCredits: 4,
        meterSubject: 'individual_user',
        dailyQuota: { free: 5, plus: 100, pro: 300 },
        includedDaily: { free: 5 },
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    cover_letter_generate: aiAction(
      'cover_letter_generate',
      'Studio cover letter',
      'Generate a saved job-specific cover letter artifact.',
      {
        key: 'cover_letter_generate',
        planSet: 'individual',
        featureKey: 'individual.cover_letter',
        countsTowardDaily: true,
        estimatedCredits: 60,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    application_packet_generate: aiAction(
      'application_packet_generate',
      'Application packet',
      'Generate a bundle of job-specific application artifacts.',
      {
        key: 'application_packet_generate',
        planSet: 'individual',
        featureKey: 'individual.application_packet',
        countsTowardDaily: true,
        estimatedCredits: 120,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    screening_answers_generate: aiAction(
      'screening_answers_generate',
      'Screening answers',
      'Draft job-specific screening question answers for review.',
      {
        key: 'screening_answers_generate',
        planSet: 'individual',
        featureKey: 'individual.application_packet',
        countsTowardDaily: true,
        estimatedCredits: 60,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.bio_generator': aiAction(
      'individual.bio_generator',
      'Bio generator',
      'Generate a professional bio.',
      {
        key: 'individual.bio_generator',
        planSet: 'individual',
        featureKey: 'individual.bio_generator',
        countsTowardDaily: true,
        estimatedCredits: 30,
        meterSubject: 'individual_user',
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.career_agent_chat': aiAction(
      'individual.career_agent_chat',
      'Career agent chat',
      'A user-visible career agent response.',
      {
        key: 'individual.career_agent_chat',
        planSet: 'individual',
        featureKey: 'individual.career_agent_chat',
        countsTowardDaily: true,
        estimatedCredits: 10,
        // Chat is token-metered with a 10-credit cap: any positive balance can
        // start a reply and the charge caps at the remaining credits, so the
        // "out of credits" state only ever happens at a true zero balance.
        allowPartialCredits: true,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    recruiter_preview_generate: aiAction(
      'recruiter_preview_generate',
      'Recruiter preview',
      'Generate an employer-facing profile and application preview.',
      {
        key: 'recruiter_preview_generate',
        planSet: 'individual',
        featureKey: 'individual.recruiter_preview',
        countsTowardDaily: true,
        estimatedCredits: 80,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.interview_prep': aiAction(
      'individual.interview_prep',
      'Interview prep',
      'Generate interview prep assistance.',
      {
        key: 'individual.interview_prep',
        planSet: 'individual',
        featureKey: 'individual.interview_prep',
        countsTowardDaily: true,
        estimatedCredits: 60,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    interview_prep_generate: aiAction(
      'interview_prep_generate',
      'Studio interview prep',
      'Generate a saved role-specific interview prep plan.',
      {
        key: 'interview_prep_generate',
        planSet: 'individual',
        featureKey: 'individual.interview_prep',
        countsTowardDaily: true,
        estimatedCredits: 80,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'individual.salary_insights': aiAction(
      'individual.salary_insights',
      'Salary insights',
      'Generate salary insight comparisons.',
      {
        key: 'individual.salary_insights',
        planSet: 'individual',
        featureKey: 'individual.salary_insights',
        countsTowardDaily: true,
        estimatedCredits: 60,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    salary_report_generate: aiAction(
      'salary_report_generate',
      'Studio salary report',
      'Generate localized salary and negotiation guidance.',
      {
        key: 'salary_report_generate',
        planSet: 'individual',
        featureKey: 'individual.salary_insights',
        countsTowardDaily: true,
        estimatedCredits: 80,
        meterSubject: 'individual_user',
        premiumOnComplexOnly: true,
        upgradePromptKey: 'individual.limit.free',
      },
    ),
    'employer.workspace_basic_ai': aiAction(
      'employer.workspace_basic_ai',
      'Workspace basic AI',
      'Basic employer AI action available on the free workspace pool.',
      {
        key: 'employer.workspace_basic_ai',
        planSet: 'employer',
        countsTowardDaily: true,
        meterSubject: 'employer_workspace',
        availableToEmployerFreeWorkspace: true,
        upgradePromptKey: 'employer.workspace.limit.free',
      },
    ),
    'employer.candidate_ranking': aiAction(
      'employer.candidate_ranking',
      'Candidate ranking',
      'Rank candidates with AI.',
      {
        key: 'employer.candidate_ranking',
        planSet: 'employer',
        featureKey: 'employer.candidate_ranking',
        countsTowardDaily: true,
        meterSubject: 'employer_seat',
        requiresPaidSeat: true,
        upgradePromptKey: 'employer.seat.required',
      },
    ),
    'employer.jd_generator': aiAction(
      'employer.jd_generator',
      'JD generator',
      'Generate a job description with AI.',
      {
        key: 'employer.jd_generator',
        planSet: 'employer',
        featureKey: 'employer.jd_generator',
        countsTowardDaily: true,
        meterSubject: 'employer_seat',
        requiresPaidSeat: true,
        upgradePromptKey: 'employer.seat.required',
      },
    ),
    'employer.bulk_screening': aiAction(
      'employer.bulk_screening',
      'Bulk screening',
      'Bulk-screen candidates with AI.',
      {
        key: 'employer.bulk_screening',
        planSet: 'employer',
        featureKey: 'employer.bulk_screening',
        countsTowardDaily: true,
        meterSubject: 'employer_seat',
        requiresPaidSeat: true,
        premiumOnComplexOnly: true,
        upgradePromptKey: 'employer.seat.required',
      },
    ),
    'employer.autopilot_hiring': aiAction(
      'employer.autopilot_hiring',
      'Autopilot hiring',
      'Run an autonomous hiring pass.',
      {
        key: 'employer.autopilot_hiring',
        planSet: 'employer',
        featureKey: 'employer.autopilot_hiring',
        countsTowardDaily: true,
        meterSubject: 'employer_seat',
        requiresPaidSeat: true,
        premiumOnComplexOnly: true,
        upgradePromptKey: 'employer.seat.required',
      },
    ),
    'onboarding.skills_suggest': aiAction(
      'onboarding.skills_suggest',
      'Onboarding skill suggestion',
      'Suggest skills during onboarding.',
      {
        key: 'onboarding.skills_suggest',
        planSet: 'onboarding',
        countsTowardDaily: false,
        meterSubject: 'none',
        upgradePromptKey: 'individual.plus',
      },
    ),
    'onboarding.highlights_generate': aiAction(
      'onboarding.highlights_generate',
      'Onboarding highlights generation',
      'Generate onboarding highlights.',
      {
        key: 'onboarding.highlights_generate',
        planSet: 'onboarding',
        countsTowardDaily: false,
        meterSubject: 'none',
        upgradePromptKey: 'individual.plus',
      },
    ),
    'onboarding.text_enhance': aiAction(
      'onboarding.text_enhance',
      'Onboarding text enhancement',
      'Enhance onboarding text.',
      {
        key: 'onboarding.text_enhance',
        planSet: 'onboarding',
        countsTowardDaily: false,
        meterSubject: 'none',
        upgradePromptKey: 'individual.plus',
      },
    ),
  },
  prompts: {
    'individual.plus': {
      key: 'individual.plus',
      title: 'Tabbio Plus',
      message:
        'Find jobs faster and stand out with more included AI usage, custom branding, and better analytics.',
      ctaLabel: 'Upgrade to Plus',
      recommendedPlanSet: 'individual',
      recommendedTier: 'plus',
    },
    'individual.pro': {
      key: 'individual.pro',
      title: 'Tabbio Pro',
      message:
        'Unlock your autopilot career agent, premium routing, and deeper career insights.',
      ctaLabel: 'Upgrade to Pro',
      recommendedPlanSet: 'individual',
      recommendedTier: 'pro',
    },
    'individual.skill_run': {
      key: 'individual.skill_run',
      title: 'Keep running skills',
      message:
        'You’ve used today’s free AI credits. Upgrade to Plus for a monthly credit pool so skills keep running.',
      ctaLabel: 'See Plus',
      recommendedPlanSet: 'individual',
      recommendedTier: 'plus',
    },
    'individual.connectors_limit': {
      key: 'individual.connectors_limit',
      title: 'Connect more services',
      message:
        'You’ve reached the free limit for connected services. Upgrade to Plus to connect unlimited tools.',
      ctaLabel: 'Upgrade to Plus',
      recommendedPlanSet: 'individual',
      recommendedTier: 'plus',
    },
    'individual.nova': {
      key: 'individual.nova',
      title: 'Automation',
      message:
        'Upgrade to Pro to turn a plain-English sentence into a scheduled automation, with drafts you always approve before anything is sent.',
      ctaLabel: 'Upgrade to Pro',
      recommendedPlanSet: 'individual',
      recommendedTier: 'pro',
    },
    'individual.limit.free': {
      key: 'individual.limit.free',
      title: 'You’ve used today’s free credits',
      message:
        'Upgrade for a monthly credit pool, or come back tomorrow for fresh daily credits.',
      ctaLabel: 'See Plus',
      recommendedPlanSet: 'individual',
      recommendedTier: 'plus',
    },
    'individual.limit.plus': {
      key: 'individual.limit.plus',
      title: 'You’re a power user',
      message:
        'Upgrade to Pro for the highest AI capacity and Career agent automation.',
      ctaLabel: 'See Pro',
      recommendedPlanSet: 'individual',
      recommendedTier: 'pro',
    },
    'individual.limit.pro': {
      key: 'individual.limit.pro',
      title: 'Heavy usage detected',
      message: 'You’ve reached today’s fair-use allowance. It resets tomorrow.',
      ctaLabel: 'View plan',
      recommendedPlanSet: 'individual',
      recommendedTier: 'pro',
    },
    'employer.plus': {
      key: 'employer.plus',
      title: 'Employer Plus',
      message:
        'Add Plus seats to unlock recruiter outreach, candidate ranking, and team analytics.',
      ctaLabel: 'Add Plus seats',
      recommendedPlanSet: 'employer',
      recommendedTier: 'plus',
    },
    'employer.pro': {
      key: 'employer.pro',
      title: 'Employer Pro',
      message:
        'Add Pro seats for autopilot hiring, bulk screening, talent insights, and premium support.',
      ctaLabel: 'Add Pro seats',
      recommendedPlanSet: 'employer',
      recommendedTier: 'pro',
    },
    'employer.workspace.limit.free': {
      key: 'employer.workspace.limit.free',
      title: 'Your workspace has used today’s AI actions',
      message:
        'Add paid seats for more AI or wait until tomorrow’s workspace reset.',
      ctaLabel: 'Add seats',
      recommendedPlanSet: 'employer',
      recommendedTier: 'plus',
    },
    'employer.seat.required': {
      key: 'employer.seat.required',
      title: 'AI is available on assigned seats only',
      message:
        'Ask your workspace owner to assign a paid AI seat or add more seats for the team.',
      ctaLabel: 'Manage seats',
      recommendedPlanSet: 'employer',
      recommendedTier: 'plus',
    },
  },
};

export function getPlanTierDefinition(planSet: PlanSet, tier: PlanTier) {
  return subscriptionCatalog.planSets[planSet].tiers[tier];
}

export function getFeatureDefinition(key: FeatureKey) {
  return subscriptionCatalog.features[key];
}

export function getAiActionDefinition(key: AIActionKey) {
  return subscriptionCatalog.aiActions[key];
}
